import { useEffect, useRef } from 'react';
import { launchFeedback, unmountFeedback } from '@ms/inapp-feedback-loader';
import {
  FeedbackAuthenticationType,
  FeedbackUiType,
  FeedbackType,
  InAppFeedbackScenarioType,
  InAppFeedbackQuestionUiType,
  InAppFeedbackQuestionUiBehaviour,
  IFeedbackInitOptions,
} from '@ms/ocv-inapp-feedback-types';
import { t } from '@/strings/index';
import { logFeedbackSubmission, logFeedbackModalOpened, logFeedbackDismissed } from '@/telemetry/telemetryEvents';
import { FEEDBACK_APP_ID, FEEDBACK_HOST_NAME, FEEDBACK_IS_PRODUCTION } from '@/constants/feedback';
import '@/styles/FeedbackForm.css';

/** Build feedbackConfig for anonymous auth (no CPS checks needed) */
function buildAnonymousFeedbackConfig(
  uiType: 'modal' | 'inline',
  questionText: string,
  feedbackSource: string
) {
  return {
    feedbackUiType: uiType === 'modal'
      ? FeedbackUiType.Modal
      : FeedbackUiType.NoSurfaceWithoutTitle,
    isDisplayed: true,
    initialFeedbackType: FeedbackType.Smile,
    isSupportEnabled: true,
    isThankYouPageDisabled: false,
    hideFooterActionButtons: false,
    scenarioConfig: {
      isScenarioEnabled: true as const,
      scenarioType: InAppFeedbackScenarioType.Custom,
      questionDetails: {
        questionUiType: InAppFeedbackQuestionUiType.Rating,
        questionUiBehaviour: [
          InAppFeedbackQuestionUiBehaviour.CommentNotRequired,
        ],
        questionInstruction: {
          displayedString: questionText,
          displayedStringInEnglish: questionText,
        },
        questionOptions: [
          { displayedString: '1', displayedStringInEnglish: '1' },
          { displayedString: '2', displayedStringInEnglish: '2' },
          { displayedString: '3', displayedStringInEnglish: '3' },
          { displayedString: '4', displayedStringInEnglish: '4' },
          { displayedString: '5', displayedStringInEnglish: '5' },
        ],
      },
    },
    appData: JSON.stringify({
      featureArea: 'Frontline-BYOD-Wizard',
      feedbackSource,
    }),
  };
}

export interface FeedbackFormProps {
  uiType: 'modal' | 'inline';
  questionText?: string;
  feedbackSource: 'thanks-screen' | 'footer' | 'device-selection';
  /** For AAD auth: Pass initOptions from useFeedbackPolicyCheck hook */
  initOptions?: IFeedbackInitOptions;
  /** For anonymous auth: Use 'anonymous' (no initOptions needed) */
  authenticationType?: 'aad' | 'anonymous';
  /** Current screen pathname (for footer-initiated feedback to track which screen the user was on) */
  currentScreen?: string;
  onReady?: () => void;
  onSubmitStart?: () => void;
  onSuccess?: (feedbackId: string) => void;
  onError?: (error: string) => void;
  onDismiss?: (wasSubmitted: boolean) => void;
}

/**
 * Thin wrapper around Microsoft InApp Feedback SDK
 * - For AAD auth: Receives initOptions from useFeedbackPolicyCheck hook (CPS already checked)
 * - For anonymous auth: Builds config directly (no CPS check needed)
 */
export default function FeedbackForm({
  uiType,
  questionText,
  feedbackSource,
  initOptions,
  authenticationType = 'aad',
  currentScreen,
  onReady,
  onSubmitStart,
  onSuccess,
  onError,
  onDismiss,
}: FeedbackFormProps) {
  const feedbackContainerRef = useRef<HTMLDivElement>(null);

  // Use refs to store callbacks to avoid re-launching SDK when callbacks change
  const onReadyRef = useRef(onReady);
  const onSubmitStartRef = useRef(onSubmitStart);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onDismissRef = useRef(onDismiss);

  onReadyRef.current = onReady;
  onSubmitStartRef.current = onSubmitStart;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onDismissRef.current = onDismiss;

  const displayQuestionText = questionText ?? t('feedback.modal.satisfaction');
  const useAnonymousAuth = authenticationType === 'anonymous';

  useEffect(() => {
    if (!feedbackContainerRef.current) return;

    // For AAD auth, initOptions must be provided (from useFeedbackPolicyCheck)
    if (!useAnonymousAuth && !initOptions) {
      console.error('FeedbackForm: AAD auth requires initOptions from useFeedbackPolicyCheck hook');
      onErrorRef.current?.('Configuration error: missing initOptions for AAD auth');
      return;
    }

    const containerElement = feedbackContainerRef.current;

    // NOTE: The SDK doesn't provide an official onSubmitStart callback
    // This is a heuristic detection that may break if SDK UI changes
    const handleClick = (e: MouseEvent) => {
      if (!onSubmitStartRef.current) return;

      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' &&
          (target.textContent?.toLowerCase().includes('submit') ||
           target.classList.contains('fui-Button') && target.closest('[class*="footer"]'))) {
        console.log('FeedbackForm: Submit button clicked (heuristic detection)');
        onSubmitStartRef.current?.();
      }
    };

    containerElement.addEventListener('click', handleClick);

    let isMounted = true;

    const launchFeedbackForm = async () => {
      try {
        const initData = {
          hostName: FEEDBACK_HOST_NAME,
          locale: "en-US",
          inAppFeedbackRef: containerElement,
        };

        // Callback functions for feedback events
        const callbackFunctions = {
          onSuccess: (feedbackId: string) => {
            console.log('FeedbackForm: Feedback submitted successfully! ID:', feedbackId);
            logFeedbackSubmission(feedbackSource, feedbackId, currentScreen);
            onSuccessRef.current?.(feedbackId);
          },
          onError: (errorMessage?: string) => {
            console.error('FeedbackForm: Feedback submission failed:', errorMessage);
            onErrorRef.current?.(errorMessage || 'Unknown error');
          },
          onDismiss: (isFeedbackSent?: boolean) => {
            console.log('FeedbackForm: Dismissed, was submitted:', isFeedbackSent);
            // Log dismissal telemetry
            logFeedbackDismissed(feedbackSource, isFeedbackSent ?? false, currentScreen);
            // For non-modal UiTypes, we must call unmountFeedback to clean up
            unmountFeedback();
            onDismissRef.current?.(isFeedbackSent ?? false);
          },
        };

        let feedbackOptions;

        if (useAnonymousAuth) {
          // Anonymous auth: build config from scratch (no CPS needed)
          console.log('FeedbackForm: Launching with anonymous auth');
          feedbackOptions = {
            appId: FEEDBACK_APP_ID,
            authenticationType: FeedbackAuthenticationType.Unauthenticated,
            isProduction: FEEDBACK_IS_PRODUCTION,
            callbackFunctions,
            feedbackConfig: buildAnonymousFeedbackConfig(uiType, displayQuestionText, feedbackSource),
            themeOptions: { baseTheme: "WindowsLight" },
          };
        } else {
          // AAD auth: use initOptions from useFeedbackPolicyCheck hook (CPS already checked)
          // But override feedbackConfig with proper Rating scenario config
          console.log('FeedbackForm: Launching with AAD auth (using pre-checked initOptions)');

          // Build proper feedback config (preserving complianceChecks from CPS check)
          const properFeedbackConfig = buildAnonymousFeedbackConfig(uiType, displayQuestionText, feedbackSource);

          feedbackOptions = {
            ...initOptions,
            callbackFunctions: {
              // IMPORTANT: Preserve getAccessToken from initOptions for CPS token refresh
              ...initOptions?.callbackFunctions,
              // Add our UI event callbacks
              ...callbackFunctions,
            },
            // Override feedbackConfig with proper scenario (initOptions has minimal config for CPS check only)
            feedbackConfig: {
              ...properFeedbackConfig,
              // Preserve complianceChecks from CPS policy check
              complianceChecks: initOptions?.feedbackConfig?.complianceChecks,
            },
          } as Parameters<typeof launchFeedback>[1];
        }

        await launchFeedback(initData, feedbackOptions);

        if (!isMounted) return;

        console.log('FeedbackForm: Feedback launched successfully');
        const authType = useAnonymousAuth ? 'anonymous' : 'aad';
        logFeedbackModalOpened(feedbackSource, authType, currentScreen);
        onReadyRef.current?.();
      } catch (error) {
        console.error('FeedbackForm: Error launching feedback:', error);
        if (!isMounted) return;
        // Still call onReady on error so UI doesn't stay in loading state forever
        onReadyRef.current?.();
      }
    };

    // Small delay to ensure DOM is fully ready before SDK initialization
    const timer = setTimeout(launchFeedbackForm, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      containerElement.removeEventListener('click', handleClick);
    };
  }, [uiType, displayQuestionText, useAnonymousAuth, initOptions, feedbackSource, currentScreen]);

  return <div ref={feedbackContainerRef} />;
}
